import { Columns3 } from "lucide-react";
import { Button } from "@/components/ui/button.jsx";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover.jsx";
import { outlineChromeButton } from "@/lib/status-styles.js";

/**
 * Popover that lets the user toggle table column visibility.
 * @param {Object} props - Component props
 * @param {{ key: string, label: string, locked?: boolean }[]} props.columns - All available columns in display order
 * @param {string[]} props.visibleKeys - Currently visible column keys
 * @param {(key: string) => void} props.onToggle - Called when a column checkbox is toggled
 * @param {() => void} props.onReset - Called when "Reset to defaults" is clicked
 * @returns {JSX.Element} Column visibility popover
 */
export function ColumnVisibilityPopover({ columns, visibleKeys, onToggle, onReset }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className={outlineChromeButton}>
          <Columns3 className='h-4 w-4' />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-56 p-2'>
        <div className='max-h-80 overflow-y-auto'>
          {columns.map((col) => {
            const checked = col.locked || visibleKeys.includes(col.key);
            return (
              <label
                key={col.key}
                className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer select-none'>
                <Checkbox
                  checked={checked}
                  disabled={col.locked}
                  onChange={() => !col.locked && onToggle(col.key)}
                  className='shrink-0'
                />
                <span className='text-sm text-foreground flex-1'>{col.label}</span>
                {col.locked && (
                  <span className='text-xs text-muted-foreground'>locked</span>
                )}
              </label>
            );
          })}
        </div>
        <div className='mt-2 pt-2 border-t border-border'>
          <button
            onClick={onReset}
            className='w-full text-left px-2 py-1.5 text-xs text-primary hover:text-primary/90 hover:bg-muted rounded transition-colors'>
            Reset to defaults
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
